import React from "react";
import Modal from "@/components/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clipboard, ExternalLink, CheckCircle2 } from "lucide-react";
import { all_routes } from "../../Router/all_routes";
import { useNavigate } from "react-router-dom";

export function DailyTasksModal({ open, onOpenChange, tasks, userName }) {
  const navigate = useNavigate();
  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return <Badge variant="danger">HIGH</Badge>;
      case "medium":
        return (
          <Badge variant="warning">MEDIUM</Badge>
        );
      case "low":
        return <Badge variant="success">LOW</Badge>;
      default:
        return <Badge variant="info">NORMAL</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return (
          <Badge variant="success">COMPLETED</Badge>
        );
      case "pending":
        return (
          <Badge
            variant="warning"
          >
            PENDING
          </Badge>
        );
      case "overdue":
        return <Badge variant="danger">OVERDUE</Badge>;
      default:
        return (
          <Badge variant="secondary">{status?.toUpperCase() || "UNKNOWN"}</Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Daily Tasks"
      icon={Clipboard}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate(all_routes.tasks);
            }}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            View All Tasks
          </Button>
        </div>
      }
    >
      <div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Good day, {userName}!</h3>
          <p className="text-muted-foreground text-sm">
            Here are your tasks to manage for today:
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {tasks && tasks.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((taskAssignment) => (
                  <TableRow key={taskAssignment.id}>
                    <TableCell>
                      <div className="font-medium">
                        {taskAssignment.task.title}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {taskAssignment.task.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {taskAssignment.employee?.full_name || "-"}
                    </TableCell>
                    <TableCell>
                      {getPriorityBadge(taskAssignment.task.priority)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(taskAssignment.due_date)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(taskAssignment.status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h4 className="text-lg font-semibold">No tasks for today!</h4>
            <p className="text-muted-foreground">
              You have no daily tasks to manage. Enjoy your day!
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
